import React from "react";
import { Outlet, useLocation, Navigate } from "react-router-dom";
import { useSubscription } from "@/subscription/useSubscription";
import { useAuth } from "@/auth/AuthProvider";
import LoadingSpinner from "@/components/LoadingSpinner";
import { isEmulator } from "@/lib/environment";

/**
 * RequireSubscription - SRL Subscription Gate
 * 
 * CONTRACT:
 * - Reads ONLY SRL subscription state (users/{uid}/subscription)
 * - Allows ONLY SRL billing routes
 * - Redirects ONLY to /billing
 * 
 * DOES NOT:
 * - Check Pro billing state
 * - Check Stripe customer summaries
 * - Redirect to /pro/*
 * - Infer entitlement from multiple sources
 * - Implement grace periods or convergence logic
 */

const ALWAYS_ALLOWED = new Set<string>([
  "/billing",
  "/profile",
  "/help",
  "/settings",
  "/legal-disclaimer"
]);

const RequireSubscription: React.FC = () => {
  const { sub } = useSubscription(); // SRL subscription only
  const { user, claims, loading: authLoading } = useAuth();
  const location = useLocation();
  const IS_DEV = import.meta.env.DEV;

  // T-50 FIX: Derive admin status directly from claims to avoid async getIdTokenResult() hang
  // The claims object is already loaded synchronously from the auth token — no extra async call needed
  const isAdmin = !!(claims?.admin || claims?.role === 'super');
  
  // DEBUG: Log subscription status
  if (IS_DEV) console.log('[RequireSubscription] 🔍 Status check:', {
    sub,
    location: location.pathname,
    authLoading,
    isAdmin,
    hasUser: !!user,
  });

  // CYPRESS BYPASS: If Cypress is present, bypass subscription check entirely
  if (typeof window !== 'undefined' && (window as any).Cypress) {
    return <Outlet />;
  }
  
  // 1. Allow always-allowed pages
  if (Array.from(ALWAYS_ALLOWED).some(p => location.pathname.startsWith(p))) {
    return <Outlet />;
  }

  // 2. Wait for auth to load
  if (authLoading) {
    return (
      <div className="flex h-full min-h-[50vh] items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  // 2b. Wait for claims to resolve when a user is present.
  // claims starts undefined and is set async after onAuthStateChanged fires.
  // Redirecting before claims arrive causes admin users to hit /billing.
  const claimsLoaded = !user || claims !== undefined;
  if (!claimsLoaded) {
    return (
      <div className="flex h-full min-h-[50vh] items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  // 2c. Wait for subscription to load (prevent race condition)
  if (sub === 'loading') {
    if (IS_DEV) console.log('[RequireSubscription] ⏳ Waiting for subscription to load...');
    return (
      <div className="flex h-full min-h-[50vh] items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  // 3. AUDIT BYPASS: Emulator-only bypass for testing
  const isEmulatorEnv = isEmulator();
  const auditBypassFlag = typeof window !== 'undefined' && window.localStorage.getItem('__AUDIT_BYPASS__') === 'true';
  const hasAuditBypassClaim = claims?.auditBypassBilling === true;
  
  if (isEmulatorEnv && (auditBypassFlag || hasAuditBypassClaim)) {
    if (IS_DEV) console.log('[RequireSubscription] ✅ AUDIT BYPASS enabled (emulator mode)');
    return <Outlet />;
  }

  // 4. Allow Admins
  if (isAdmin) {
    return <Outlet />;
  }

  // 5. Check SRL Subscription - single source of truth
  const srlActive = 
    sub === 'active' || 
    sub === 'trialing';
  if (IS_DEV) console.log('[RequireSubscription] 📊 Subscription check:', { sub, srlActive });

  if (srlActive) {
    if (IS_DEV) console.log('[RequireSubscription] ✅ Active subscription, allowing access');
    return <Outlet />;
  }

  // 6. Redirect to billing if no valid SRL subscription
  if (IS_DEV) console.log('[RequireSubscription] ❌ No active subscription, redirecting to /billing');
  if (IS_DEV) console.warn('[RequireSubscription] blocking', { uid: user?.uid, hasSubscription: srlActive, path: location.pathname });
  // 6. Redirect to billing if no valid SRL subscription
  return <Navigate to="/billing" state={{ from: location }} replace />;
};

export default RequireSubscription;